<script lang="ts" setup>
import { computed } from 'vue';
import { AppTypography } from '~/components/base';

const { reportType, reportId, returnTo } = defineProps<{
  reportType: 'candidates' | 'parties' | 'campaigns';
  reportId?: string;
  /**
   * Where the report page's Back should return to, when that is not simply the
   * entity's own Details page. Desktop opens a campaign as a popup over
   * /campaigns (VTW-497), so only the opener knows the URL that reproduces
   * what the reporter was looking at.
   */
  returnTo?: string;
}>();

const target = computed(() => ({
  path: '/report-a-problem',
  query: {
    report_type: reportType,
    report_id: reportId,
    // Omitted rather than sent empty, so the common case keeps a clean URL.
    ...(returnTo ? { return_to: returnTo } : {}),
  },
}));
</script>

<template>
  <NuxtLink
    :to="target"
    data-testid="report-problem-link"
    class="report-problem-link flex w-fit items-center gap-2 no-underline"
  >
    <Icon name="solar:flag-linear" class="shrink-0" />
    <AppTypography type="subheadline-bold" element="span">
      Help us improve this information
    </AppTypography>
  </NuxtLink>
</template>

<style scoped lang="scss">
.report-problem-link {
  border-radius: $radius-md;
  // Figma: the muted brick red #b85450, not this app's $semantic-color-error
  // (#f10021).
  color: #b85450;

  // Figma: sized to match the Sources button above it (VTW-558) — the
  // previous 40px/$spacing-sm read visibly smaller than that neighbor.
  // No left padding: unlike the Sources button, this isn't drawn as a
  // bordered box, so its own inset was reading as misaligned against the
  // page's plain body text (title, bio) sitting flush at the page gutter.
  @media (max-width: 639.98px) {
    height: 48px;
    padding: $spacing-md $spacing-lg $spacing-md 0;
  }
}
</style>
